import {
  $apiClient,
  $businessUnit,
  ApiProvider,
  AsyncCacheProvider,
  AuthProvider,
  createNewUser,
  getRedirectUrl,
  HistoryProvider,
  ModuleFederationContextProvider,
  onSigninCallback,
  useUrlContext,
} from '@msdk/common'
import { I18nProvider, ModalContextProvider } from '@msdk/forms'
import { ComposerI18nWrapper } from '@msdk/forms/src/components/I18nWrapper'
import { PageInformation } from '@msdk/functional-components'
import { ErrorBoundaryWrapper } from '@msdk/telemetry'
import { ApplicationHeaderWrapper, BannerProps, Box, COLORS, ResetCSS, styled, THEME_CONST } from '@msdk/ui'
import { AppInitialized } from 'components/AppInitialized'
import { BannerInjector } from 'components/BannerInjector'
import { Breadcrumb } from 'components/Breadcrumb'
import { ContentIntialized } from 'components/ContentInitialized'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { FullScreenLoader } from 'components/FullScreenLoader'
import { Header } from 'components/Header'
import { IdTaggingWrapper } from 'components/IdTaggingWrapper'
import { SideMenu } from 'components/SideMenu'
import { SigninError } from 'components/SigninError'
import { TrainingWrapper } from 'components/TrainingWrapper'
import { AuthSuspense } from 'providers'
import { NotificationContextProvider } from 'providers/notification-provider/NotificationContextProvider'
import { Suspense, useEffect, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

const Content = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background-color: ${COLORS.components.viewContent.background};
  padding: ${THEME_CONST.navHeight}px 0 0 ${THEME_CONST.sideMenuWidth}px;
  overflow: auto;
`

function App() {
  const { pathname, search } = useLocation()
  const client = useRecoilValue($apiClient)
  const buId = useRecoilValue($businessUnit)
  const urlCtx = useUrlContext()
  useEffect(() => {
    urlCtx.update(pathname + search)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, buId])

  const [banner, setBanner] = useState<BannerProps>()
  const [signinError, setSigninError] = useState(false)

  const onSigninError = (error: Error) => {
    console.error(error.message, error)
    setSigninError(true)
  }

  return (
    <ErrorBoundaryWrapper>
      <ResetCSS />
      <AsyncCacheProvider>
        <NotificationContextProvider>
          {signinError && <SigninError />}
          {!signinError && (
            <AuthProvider
              autoSignInArgs={{ redirect_uri: `${window.__APP_CONFIG__.CLIENT}/${getRedirectUrl()}` }}
              userManager={createNewUser()}
              onSignIn={onSigninCallback}
              onSignInError={onSigninError}
            >
              <Suspense fallback={<FullScreenLoader banner={banner} />}>
                <AuthSuspense fallback={<FullScreenLoader banner={banner} />}>
                  <IdTaggingWrapper>
                    <HelmetProvider>
                      <ErrorBoundary path={pathname + search}>
                        <ApiProvider client={client}>
                          <AppInitialized>
                            <ModuleFederationContextProvider fallback={<FullScreenLoader banner={banner} />}>
                              <I18nProvider>
                                <ComposerI18nWrapper>
                                  <ModalContextProvider>
                                    <HistoryProvider>
                                      <ContentIntialized>
                                        <TrainingWrapper onBanner={setBanner}>
                                          <PageInformation pageTitle="" />
                                          <Suspense fallback={<ApplicationHeaderWrapper />}>
                                            <Header />
                                          </Suspense>
                                          <SideMenu />
                                          <Content data-test-id="SHELL-CONTENT">
                                            <Suspense fallback={<FullScreenLoader />}>
                                              <Box>
                                                <Breadcrumb />
                                                <BannerInjector />
                                              </Box>
                                              <ScrollRestoration />
                                              <Outlet />
                                            </Suspense>
                                          </Content>
                                        </TrainingWrapper>
                                      </ContentIntialized>
                                    </HistoryProvider>
                                  </ModalContextProvider>
                                </ComposerI18nWrapper>
                              </I18nProvider>
                            </ModuleFederationContextProvider>
                          </AppInitialized>
                        </ApiProvider>
                      </ErrorBoundary>
                    </HelmetProvider>
                  </IdTaggingWrapper>
                </AuthSuspense>
              </Suspense>
            </AuthProvider>
          )}
        </NotificationContextProvider>
      </AsyncCacheProvider>
    </ErrorBoundaryWrapper>
  )
}

export default App
